/**
 * THE sort comparators for the order-list pages and the hires Table — ONE
 * definition (2026-09-28, owner: share what can be shared so the tables cannot
 * diverge again).
 *
 * Before this the rule was written four times: the `switch (sortField)`
 * reducers in `pages/DepartmentOrders.js`, `pages/MyOrders.js` and
 * `pages/Approvals.js` (identical but for their comments), and
 * `sortHireRows` in `components/hires/HireTableView.js`. Plus four copies of the
 * header-click rule (`handleSort` × 3, `useHireTableList`'s `onHeaderSort`).
 *
 * 🔑 **EXTRACTION, NOT UNIFICATION — every result is IDENTICAL to before, on
 * every page, for every field and direction.** The order pages and the hires
 * Table genuinely differ, and each difference is a PARAMETER here, never a
 * silent merge:
 *   - BLANK: falsy on the order pages (`isFalsyBlank` — a PO of the NUMBER `0`
 *     is blank there), nullish-or-`''` on hires (`isNullishBlank` — `0` is a
 *     value there).
 *   - DIRECTION: the order pages read anything but `'asc'` as DESCENDING
 *     (`orderSortSign`); hires reads anything but `'desc'` as ASCENDING
 *     (`hireSortSign`). Only reachable with a missing direction, and kept.
 *   - TEXT: `<` on lowercased strings for orders; `localeCompare` for hires.
 *   - PO TIE-BREAK: raw `id` for an order row; `Number(order_number)` for a
 *     hire row (a hire row's own id is not its order's).
 *   - The order pages' `default` branch puts BLANKS FIRST ascending — a known,
 *     recorded wart (`pages/CLAUDE.md`, `orderSortOptionsHaveHandlers.test.js`),
 *     preserved.
 *   - Hires-only: OVERDUE FIRST whatever the field (owner ruling) and the
 *     RANKED status — both in `sortHireRows`, built on `compareRanked` here.
 *
 * Pinned by the two characterisation goldens captured on the pre-extraction
 * code: `pages/orderSort.characterisation.test.js` (all three pages, every
 * reachable field, both directions — Approvals' only sort pin) and
 * `components/hires/hireSort.characterisation.test.js`.
 *
 * 🪤 `orderSortOptionsHaveHandlers.test.js` reads the `case '…':` labels of the
 * ONE `switch (sortField)` block in THIS file, and requires each order page to
 * call `compareOrderRows` and carry no switch of its own. Keep exactly one.
 */

/** The order pages' blank: falsy (`!v`). `0` IS blank here. */
export const isFalsyBlank = (v) => !v;
/** The hires Table's blank: `null`/`undefined`/`''`. `0` is a VALUE here. */
export const isNullishBlank = (v) => v == null || v === '';

/** Order pages: `'asc'` → +1, ANYTHING else → −1. */
export const orderSortSign = (sortDir) => (sortDir === 'asc' ? 1 : -1);
/** Hires: `'desc'` → −1, ANYTHING else → +1. */
export const hireSortSign = (sortDir) => (sortDir === 'desc' ? -1 : 1);

/** `<` / `>` with no coercion — the order reducers' own comparison. */
export const compareRaw = (x, y) => {
  if (x < y) return -1;
  if (x > y) return 1;
  return 0;
};

const signed = (c, sign) => (c === 0 ? 0 : c * sign);

/**
 * Exactly one of the two blank → the blank one LAST, in BOTH directions (the
 * return happens before any sign is applied). `0` when both or neither are
 * blank, so the caller decides what "both blank" means.
 */
export const oneBlankLast = (aBlank, bBlank) => {
  if (aBlank === bBlank) return 0;
  return aBlank ? 1 : -1;
};

/**
 * A sparse TEXT column, blanks last in both directions, case-insensitive, with
 * an optional tie-break that FOLLOWS the arrow. THE PO comparator on both
 * tables (`tieBreak` = the order-number key), and the order pages' whole
 * blanks-last group (no `tieBreak`).
 */
export function compareBlanksLastText(a, b, field, sign, { isBlank, tieBreak = null }) {
  const av = a[field];
  const bv = b[field];
  const aBlank = isBlank(av);
  const bBlank = isBlank(bv);
  const blank = oneBlankLast(aBlank, bBlank);
  if (blank) return blank;
  const sa = aBlank ? '' : String(av).toLowerCase();
  const sb = bBlank ? '' : String(bv).toLowerCase();
  let c = compareRaw(sa, sb);
  if (c === 0 && tieBreak) c = tieBreak(a, b);
  return signed(c, sign);
}

/** An order row's PO tie-break: the id, compared raw. */
export const orderIdTieBreak = (a, b) => compareRaw(a.id, b.id);

/**
 * A RANKED column (hires `hire_status`): an unranked value sorts LAST in both
 * directions, two unranked tie.
 */
export function compareRanked(ra, rb, sign) {
  const na = !Number.isFinite(ra);
  const nb = !Number.isFinite(rb);
  if (na || nb) return na === nb ? 0 : (na ? 1 : -1);
  if (ra !== rb) return (ra - rb) * sign;
  return 0;
}

/**
 * THE order-list comparator — Department Orders, My Orders and To Approve.
 * The rules behind each `case` are argued at length in `pages/CLAUDE.md`'s
 * sanctioned-exception box and in `components/OrderTableView.js`.
 */
export function compareOrderRows(a, b, sortField, sortDir) {
  const sign = orderSortSign(sortDir);
  switch (sortField) {
    case 'order_date': {
      const ad = a.order_date || '';
      const bd = b.order_date || '';
      // Equal dates tie-break on the globally-unique `id`, NUMERICALLY.
      if (ad === bd) return signed(compareRaw(Number(a.id) || 0, Number(b.id) || 0), sign);
      return signed(compareRaw(ad, bd), sign);
    }
    case 'min_slack_days': {
      // `== null`, NEVER a falsy test: `0` is "order today", the most urgent.
      const aNull = a.min_slack_days == null;
      const bNull = b.min_slack_days == null;
      if (aNull && bNull) return 0;
      if (aNull) return 1;
      if (bNull) return -1;
      return signed(compareRaw(a.min_slack_days, b.min_slack_days), sign);
    }
    case 'order_total':
      return signed(compareRaw(a.order_total || 0, b.order_total || 0), sign);
    case 'id':
      return signed(compareRaw(a.id, b.id), sign);
    case 'status':
      return signed(compareRaw(a.status || '', b.status || ''), sign);
    // THE blanks-last group. `min_slack_days` must NEVER join it — `0` is a
    // real slack value and `!0` is true (it has its own `== null` case above).
    case 'required_by_date':
    case 'po_number':
    case 'job_rig':
    case 'payment_types':
    case 'first_item_cost_code':
      return compareBlanksLastText(a, b, sortField, sign, {
        isBlank: isFalsyBlank,
        tieBreak: sortField === 'po_number' ? orderIdTieBreak : null,
      });
    default:
      return signed(compareRaw(
        (a[sortField] || '').toString().toLowerCase(),
        (b[sortField] || '').toString().toLowerCase(),
      ), sign);
  }
}

/** The order pages' first-click direction: newest/biggest first for date and total. */
export const orderInitialSortDir = (field) => (
  field === 'order_date' || field === 'order_total' ? 'desc' : 'asc'
);
/** The hires Table's first-click direction: always ascending. */
export const hireInitialSortDir = () => 'asc';

/**
 * THE header/dropdown click rule, shared by all four tables. An explicit
 * direction (a CYCLING column's fixed per-field direction) returns BEFORE the
 * toggle; the same field toggles; a new field takes `initialDir(field)`.
 */
export function applySortClick(field, explicitDir, current, { setSortField, setSortDir, initialDir }) {
  if (explicitDir) {
    setSortField(field);
    setSortDir(explicitDir);
    return;
  }
  if (current === field) {
    setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  } else {
    setSortField(field);
    setSortDir(initialDir(field));
  }
}
